import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRightLeft, Check, Loader2, Mail, MessageCircle, Paperclip, Pencil, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { TAB_LABELS } from "./InboxDialogs";

function EmailDraft({ a, onDone }) {
  const wa = a.kind === "auto_whatsapp";
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ to: a.to, subject: a.subject, body: a.body });
  const [busy, setBusy] = useState(false);
  const save = async () => { await api.put(`/approvals/${a.id}`, f); setEdit(false); toast.success("Brouillon modifié"); onDone(); };
  const send = async () => {
    if (!window.confirm(a.simulation ? `Simuler l'envoi à ${f.to}${wa ? " par WhatsApp" : ""} (rien ne partira) ?` : `Envoyer cet email à ${f.to} ?`)) return;
    setBusy(true);
    try {
      if (edit) await api.put(`/approvals/${a.id}`, f);
      const r = await api.post(`/approvals/${a.id}/approve`);
      toast.success(r.data?.simulated ? "Envoi simulé — rien n'est parti réellement" : "Email envoyé"); onDone();
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible"); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-2 space-y-2">
      {edit ? (
        <div className="space-y-1.5">
          <input value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="input-field text-sm w-full" data-testid={`approval-to-${a.id}`} />
          {!wa && <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} className="input-field text-sm w-full" data-testid={`approval-subject-${a.id}`} />}
          <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={8} className="input-field text-sm w-full" data-testid={`approval-body-${a.id}`} />
        </div>
      ) : (
        <div className="text-xs rounded border border-norea-border bg-norea-bg/40 p-3">
          <div>{wa ? <span className="inline-flex items-center gap-1 text-emerald-700"><MessageCircle className="w-3 h-3" />WhatsApp</span> : "Email"} à : <strong>{(wa ? (a.to || "").split(",").map((n) => `+${n.trim()}`).join(", ") : a.to) || "—"}</strong>{!wa && <> — <strong>{a.subject}</strong></>}</div>
          <pre className="mt-1 whitespace-pre-wrap font-sans text-norea-text">{a.body}</pre>
        </div>
      )}
      {[...(a.missing_files || []).map((x) => `pas encore déposé : ${x}`), ...(a.no_email || []).map((x) => `sans email : ${x}`), ...(a.missing || []).map((m) => `variable vide : ${m.label}`)].map((w) => (
        <div key={w} className="text-[11px] text-amber-800 inline-flex items-center gap-1 mr-3" data-testid={`approval-warning-${a.id}`}><AlertTriangle className="w-3 h-3" />{w}</div>
      ))}
      {a.piece_labels?.length > 0 && <div className="text-[11px] text-norea-textMuted inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />Pièces jointes : {a.piece_labels.join(", ")}</div>}
      <div className="flex gap-2">
        <button onClick={send} disabled={busy} className="btn-primary text-xs" data-testid={`approval-send-${a.id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {a.simulation ? "Approuver (envoi simulé)" : "Approuver et envoyer"}</button>
        {edit ? <button onClick={save} className="btn-secondary text-xs" data-testid={`approval-save-${a.id}`}>Enregistrer</button>
          : <button onClick={() => setEdit(true)} className="btn-secondary text-xs" data-testid={`approval-edit-${a.id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>}
      </div>
    </div>
  );
}

export function ApprovalItem({ a, onDone }) {
  const act = async (what) => {
    try { await api.post(`/approvals/${a.id}/${what}`); toast.success(what === "approve" ? (a.kind === "field" ? "Champ mis à jour" : "Statut appliqué") : "Refusé"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Impossible"); }
  };
  return (
    <li className="p-4 border border-norea-border rounded-md bg-white" data-testid={`approval-${a.id}`}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 w-6 h-6 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center shrink-0">{a.kind === "status" ? <ArrowRightLeft className="w-3.5 h-3.5" /> : a.automation_id ? <Zap className="w-3.5 h-3.5" /> : <Mail className="w-3.5 h-3.5" />}</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-norea-textMuted flex flex-wrap gap-2">
            <Link to={`/dossiers/${a.dossier_id}`} className="font-medium text-norea-primary hover:underline">{a.dossier?.label || a.dossier_id}</Link>
            <span>onglet {TAB_LABELS[a.tab] || a.tab}</span>
            {["sav_email", "desinst_email", "planning_om"].includes(a.kind) ? <span data-testid={`approval-sav-${a.id}`}>· {a.automation_name}</span> : a.automation_id ? <span data-testid={`approval-auto-${a.id}`}>· automatisation « {a.automation_name} » — {TAB_LABELS[a.trigger_tab] || "onglet"} passé à « {a.status_label} »</span>
              : <span>· suite à « {a.email_subject} » ({a.from})</span>}
          </div>
          {a.kind === "field" ? (
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <span className="text-sm">Valeur lue pour « {a.field_label} » : {String(a.before ?? "—")} → <strong>{String(a.value)}</strong></span>
              <button onClick={() => act("approve")} className="btn-primary text-xs" data-testid={`approval-approve-${a.id}`}><Check className="w-3.5 h-3.5" /> Approuver</button>
            </div>
          ) : a.kind === "status" ? (
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              <span className="text-sm">Statut proposé{a.automation_id ? ` (onglet ${TAB_LABELS[a.tab] || a.tab})` : ""} : {a.before_label} → <strong>{a.label}</strong></span>
              <button onClick={() => act("approve")} className="btn-primary text-xs" data-testid={`approval-approve-${a.id}`}><Check className="w-3.5 h-3.5" /> Approuver</button>
            </div>
          ) : <EmailDraft a={a} onDone={onDone} />}
        </div>
        <button onClick={() => act("reject")} className="btn-ghost text-xs" data-testid={`approval-reject-${a.id}`}><X className="w-3.5 h-3.5" /> Refuser</button>
      </div>
    </li>
  );
}

export default function ApprovalsList({ limit, onCount, savId, refreshKey, dossierId, tab }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/approvals", { params: { ...(savId ? { sav_id: savId } : {}), ...(dossierId ? { dossier_id: dossierId, tab } : {}) } }).then((r) => { setData(r.data); onCount?.(r.data.count); }).catch(() => setData({ items: [], count: 0 }));
  useEffect(() => { load(); }, [savId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="py-6 flex justify-center"><Loader2 className="w-4 h-4 animate-spin" /></div>;
  const items = limit ? data.items.slice(0, limit) : data.items;
  if (!items.length) return <div className="py-6 text-center text-sm italic text-norea-textMuted" data-testid="approvals-empty">Rien à approuver.</div>;
  return <ul className="space-y-2" data-testid="approvals-list">{items.map((a) => <ApprovalItem key={a.id} a={a} onDone={load} />)}</ul>;
}
